import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
  BarElement
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import Sidebar from '../components/shared/Sidebar';
import googleReviewsService from '../services/googleReviewsService';
import { GoogleReviewsDashboardData } from '../types/googleReviews';
import './GoogleReviewsDashboardPage.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Tooltip, Legend);

type KeywordTab = 'all' | 'staff' | 'topic' | 'experience';

const KEYWORD_TABS: Array<{ key: KeywordTab; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'staff', label: 'Staff' },
  { key: 'topic', label: 'Topic' },
  { key: 'experience', label: 'Experience' }
];

function areBusinessOptionsEqual(
  current: GoogleReviewsDashboardData['businessOptions'],
  next: GoogleReviewsDashboardData['businessOptions']
): boolean {
  if (current.length !== next.length) return false;
  return current.every(
    (item, index) =>
      item.businessName === next[index]?.businessName &&
      item.reviewCount === next[index]?.reviewCount
  );
}

const GoogleReviewsDashboardPage: React.FC = () => {
  const [data, setData] = useState<GoogleReviewsDashboardData | null>(null);
  const [businessOptions, setBusinessOptions] = useState<GoogleReviewsDashboardData['businessOptions']>([]);
  const [selectedBusinessNames, setSelectedBusinessNames] = useState<string[] | null>(null);
  const [isBusinessDropdownOpen, setIsBusinessDropdownOpen] = useState(false);
  const [keywordTab, setKeywordTab] = useState<KeywordTab>('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const businessFilterRef = useRef<HTMLDivElement | null>(null);
  const selectAllRef = useRef<HTMLInputElement | null>(null);

  const allBusinessNames = useMemo(
    () => businessOptions.map((option) => option.businessName),
    [businessOptions]
  );
  const allBusinessSet = useMemo(() => new Set(allBusinessNames), [allBusinessNames]);

  const selectedBusinesses = useMemo(() => {
    if (selectedBusinessNames === null) {
      return allBusinessNames;
    }
    return selectedBusinessNames.filter((name) => allBusinessSet.has(name));
  }, [allBusinessNames, allBusinessSet, selectedBusinessNames]);

  const hasBusinessOptions = allBusinessNames.length > 0;
  const allSelected = hasBusinessOptions && selectedBusinesses.length === allBusinessNames.length;
  const noneSelected = hasBusinessOptions && selectedBusinesses.length === 0;
  const isSelectAllIndeterminate = !allSelected && selectedBusinesses.length > 0;

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = isSelectAllIndeterminate;
    }
  }, [isSelectAllIndeterminate]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (businessFilterRef.current && !businessFilterRef.current.contains(event.target as Node)) {
        setIsBusinessDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    if (selectedBusinessNames === null) return;
    setSelectedBusinessNames((current) => {
      if (!current) return current;
      const filtered = current.filter((name) => allBusinessSet.has(name));
      const isUnchanged =
        filtered.length === current.length && filtered.every((name, index) => name === current[index]);
      return isUnchanged ? current : filtered;
    });
  }, [allBusinessSet, selectedBusinessNames]);

  const loadDashboard = useCallback(async () => {
    const shouldSkipFetchForNoSelection = hasBusinessOptions && noneSelected;
    if (shouldSkipFetchForNoSelection) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const dashboard = await googleReviewsService.getDashboardData(
        allSelected || !hasBusinessOptions
          ? {}
          : { businessNames: selectedBusinesses }
      );
      setData(dashboard);
      setBusinessOptions((current) => {
        const incoming = dashboard.businessOptions || [];
        return areBusinessOptionsEqual(current, incoming) ? current : incoming;
      });
    } catch (loadError: any) {
      setError(loadError.message || 'Failed to load Google Reviews dashboard.');
    } finally {
      setLoading(false);
    }
  }, [allSelected, hasBusinessOptions, noneSelected, selectedBusinesses]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const selectedBusinessSummary = useMemo(() => {
    if (!hasBusinessOptions) return 'No businesses available';
    if (selectedBusinesses.length === 0) return 'No businesses selected';
    if (selectedBusinesses.length === allBusinessNames.length) return 'All businesses';
    return `${selectedBusinesses.length} selected`;
  }, [allBusinessNames.length, hasBusinessOptions, selectedBusinesses.length]);

  const sentimentMix = useMemo(() => {
    const map = new Map((data?.sentimentBreakdown || []).map((item) => [item.sentiment.toLowerCase(), item.count]));
    return {
      positive: map.get('positive') || 0,
      neutral: map.get('neutral') || 0,
      negative: map.get('negative') || 0
    };
  }, [data]);

  const luminaKpis = data?.luminaKpis;
  const positiveCount = luminaKpis?.positiveCount ?? sentimentMix.positive;
  const neutralCount = luminaKpis?.neutralCount ?? sentimentMix.neutral;
  const negativeCount = luminaKpis?.negativeCount ?? sentimentMix.negative;
  const averageLuminaScore = luminaKpis?.averageLuminaScore ?? data?.latestAnalysis?.averageLuminaScore ?? null;
  const positivePercent = luminaKpis?.positivePercent ?? 0;
  const analyzedReviewCount = luminaKpis?.analyzedReviewCount ?? 0;
  const analysisCoverage = data?.analysisCoverage?.coveragePercent ?? 0;

  const trendChartData = useMemo(() => ({
    labels: (data?.dailyReviewTrend30d || []).map((item) => item.day),
    datasets: [
      {
        label: 'Reviews per day',
        data: (data?.dailyReviewTrend30d || []).map((item) => item.count),
        borderColor: '#3b82f6',
        backgroundColor: 'rgba(59, 130, 246, 0.15)',
        tension: 0.3,
        fill: true
      }
    ]
  }), [data]);

  const distributionChartData = useMemo(() => ({
    labels: (data?.ratingDistribution || [])
      .slice()
      .sort((a, b) => a.rating - b.rating)
      .map((item) => `${item.rating} star`),
    datasets: [
      {
        label: 'Review count',
        data: (data?.ratingDistribution || [])
          .slice()
          .sort((a, b) => a.rating - b.rating)
          .map((item) => item.count),
        backgroundColor: ['#ef4444', '#f97316', '#eab308', '#22c55e', '#10b981']
      }
    ]
  }), [data]);

  const luminaDistributionChartData = useMemo(() => ({
    labels: (data?.luminaDistribution || [])
      .slice()
      .sort((a, b) => a.score - b.score)
      .map((item) => `Score ${item.score}`),
    datasets: [
      {
        label: 'Analyzed review count',
        data: (data?.luminaDistribution || [])
          .slice()
          .sort((a, b) => a.score - b.score)
          .map((item) => item.count),
        backgroundColor: '#8b5cf6'
      }
    ]
  }), [data]);

  const companyComparisonChartData = useMemo(() => ({
    labels: (data?.companyComparison || []).map((item) => item.businessName),
    datasets: [
      {
        label: 'Review count',
        data: (data?.companyComparison || []).map((item) => item.reviewCount),
        backgroundColor: 'rgba(59, 130, 246, 0.45)',
        borderColor: '#3b82f6',
        yAxisID: 'yReviews'
      },
      {
        label: 'Average rating',
        data: (data?.companyComparison || []).map((item) => item.averageRating ?? 0),
        backgroundColor: 'rgba(16, 185, 129, 0.45)',
        borderColor: '#10b981',
        yAxisID: 'yScores'
      },
      {
        label: 'Average lumina',
        data: (data?.companyComparison || []).map((item) => item.averageLuminaScore ?? 0),
        backgroundColor: 'rgba(139, 92, 246, 0.45)',
        borderColor: '#8b5cf6',
        yAxisID: 'yScores'
      }
    ]
  }), [data]);

  const filteredKeywordBreakdown = useMemo(() => {
    const rows = data?.keywordBreakdown || [];
    if (keywordTab === 'all') return rows;
    return rows.filter((item) => item.type.toLowerCase() === keywordTab);
  }, [data, keywordTab]);

  const handleToggleSelectAll = (checked: boolean) => {
    setSelectedBusinessNames(checked ? null : []);
  };

  const handleToggleBusiness = (businessName: string) => {
    setSelectedBusinessNames((current) => {
      const baseline = current === null ? allBusinessNames : current;
      const exists = baseline.includes(businessName);
      const next = exists
        ? baseline.filter((name) => name !== businessName)
        : [...baseline, businessName];
      return allBusinessNames.filter((name) => next.includes(name));
    });
  };

  return (
    <div className="dashboard-page">
      <Sidebar />
      <div className="dashboard-main">
        <div className="dashboard-content">
          <div className="page-header">
            <h1>Google Reviews Dashboard</h1>
          </div>

          <section className="google-reviews-dashboard-filter-row google-reviews-dashboard-filter-row-top">
            <div className="google-reviews-dashboard-business-filter" ref={businessFilterRef}>
              <span className="google-reviews-dashboard-filter-label">Filter dashboard by business</span>
              <button
                type="button"
                className="google-reviews-dashboard-business-filter-trigger"
                onClick={() => setIsBusinessDropdownOpen((open) => !open)}
                aria-expanded={isBusinessDropdownOpen}
                disabled={loading}
              >
                <span>{selectedBusinessSummary}</span>
                <span className="google-reviews-dashboard-business-filter-trigger-arrow">▾</span>
              </button>
              {isBusinessDropdownOpen && (
                <div className="google-reviews-dashboard-business-filter-menu">
                  <label className="google-reviews-dashboard-business-option google-reviews-dashboard-business-option-all">
                    <input
                      ref={selectAllRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={(event) => handleToggleSelectAll(event.target.checked)}
                    />
                    <span>Select All</span>
                  </label>
                  <div className="google-reviews-dashboard-business-option-list">
                    {businessOptions.map((option) => (
                      <label key={option.businessName} className="google-reviews-dashboard-business-option">
                        <input
                          type="checkbox"
                          checked={selectedBusinesses.includes(option.businessName)}
                          onChange={() => handleToggleBusiness(option.businessName)}
                        />
                        <span>{option.businessName}</span>
                        <small>{option.reviewCount}</small>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <button
              type="button"
              className="google-reviews-dashboard-refresh-button"
              onClick={loadDashboard}
              disabled={loading}
            >
              {loading ? 'Refreshing...' : 'Refresh Data'}
            </button>
          </section>

          {loading && <div className="google-reviews-dashboard-state">Loading dashboard...</div>}
          {error && <div className="google-reviews-dashboard-state google-reviews-dashboard-error">{error}</div>}

          {!loading && !error && noneSelected && (
            <div className="google-reviews-dashboard-state">
              No businesses selected. Select at least one business to view dashboard data.
            </div>
          )}

          {!loading && !error && !data && (
            <div className="google-reviews-dashboard-state">
              No dashboard data available yet.
            </div>
          )}

          {!loading && !error && data && !noneSelected && (
            <>
              <section className="google-reviews-dashboard-kpi-grid">
                <article className="google-reviews-dashboard-kpi-card">
                  <span>Total Reviews</span>
                  <strong>{data.totals.totalReviews}</strong>
                </article>
                <article className="google-reviews-dashboard-kpi-card">
                  <span>Businesses</span>
                  <strong>{data.totals.totalBusinesses}</strong>
                </article>
                <article className="google-reviews-dashboard-kpi-card">
                  <span>Average Rating</span>
                  <strong>{data.totals.averageRating ?? '-'}</strong>
                </article>
                <article className="google-reviews-dashboard-kpi-card">
                  <span>Average Lumina</span>
                  <strong>{averageLuminaScore ?? '-'}</strong>
                </article>
                <article className="google-reviews-dashboard-kpi-card">
                  <span>Positive / Neutral / Negative</span>
                  <strong>
                    {positiveCount} / {neutralCount} / {negativeCount}
                  </strong>
                </article>
                <article className="google-reviews-dashboard-kpi-card">
                  <span>Positive %</span>
                  <strong>{Number(positivePercent).toFixed(2)}%</strong>
                </article>
                <article className="google-reviews-dashboard-kpi-card">
                  <span>Analyzed Reviews</span>
                  <strong>{analyzedReviewCount}</strong>
                </article>
                <article className="google-reviews-dashboard-kpi-card">
                  <span>Analysis Coverage</span>
                  <strong>{Number(analysisCoverage).toFixed(2)}%</strong>
                </article>
              </section>

              <section className="google-reviews-dashboard-chart-grid">
                <article className="google-reviews-dashboard-chart-card">
                  <h3>Rating Distribution</h3>
                  {data.ratingDistribution.length ? (
                    <div className="google-reviews-dashboard-chart-canvas">
                      <Bar
                        data={distributionChartData}
                        options={{ responsive: true, maintainAspectRatio: false }}
                      />
                    </div>
                  ) : (
                    <p className="google-reviews-dashboard-empty">No rating distribution data yet.</p>
                  )}
                </article>

                <article className="google-reviews-dashboard-chart-card">
                  <h3>Lumina Distribution</h3>
                  {(data.luminaDistribution || []).length ? (
                    <div className="google-reviews-dashboard-chart-canvas">
                      <Bar
                        data={luminaDistributionChartData}
                        options={{ responsive: true, maintainAspectRatio: false }}
                      />
                    </div>
                  ) : (
                    <p className="google-reviews-dashboard-empty">No lumina distribution data yet.</p>
                  )}
                </article>

                <article className="google-reviews-dashboard-chart-card">
                  <h3>30-Day Review Trend</h3>
                  {data.dailyReviewTrend30d.length ? (
                    <div className="google-reviews-dashboard-chart-canvas">
                      <Line
                        data={trendChartData}
                        options={{ responsive: true, maintainAspectRatio: false }}
                      />
                    </div>
                  ) : (
                    <p className="google-reviews-dashboard-empty">No trend points for the last 30 days.</p>
                  )}
                </article>

                <article className="google-reviews-dashboard-chart-card">
                  <h3>Company Comparison</h3>
                  {(data.companyComparison || []).length ? (
                    <div className="google-reviews-dashboard-chart-canvas">
                      <Bar
                        data={companyComparisonChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            yReviews: {
                              type: 'linear',
                              position: 'left',
                              beginAtZero: true,
                              title: { display: true, text: 'Review Count' }
                            },
                            yScores: {
                              type: 'linear',
                              position: 'right',
                              beginAtZero: true,
                              suggestedMax: 10,
                              grid: { drawOnChartArea: false },
                              title: { display: true, text: 'Rating / Lumina' }
                            }
                          }
                        }}
                      />
                    </div>
                  ) : (
                    <p className="google-reviews-dashboard-empty">No company comparison data yet.</p>
                  )}
                </article>
              </section>

              <section className="google-reviews-dashboard-list-card">
                <div className="google-reviews-dashboard-section-header">
                  <h3>Trending Phrases</h3>
                  <div className="google-reviews-dashboard-tabs">
                    {KEYWORD_TABS.map((tab) => (
                      <button
                        key={tab.key}
                        type="button"
                        className={`google-reviews-dashboard-tab ${keywordTab === tab.key ? 'is-active' : ''}`}
                        onClick={() => setKeywordTab(tab.key)}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                </div>
                {filteredKeywordBreakdown.length === 0 ? (
                  <p className="google-reviews-dashboard-empty">No keyword data available for this view.</p>
                ) : (
                  <table className="google-reviews-dashboard-table">
                    <thead>
                      <tr>
                        <th>Phrase</th>
                        <th>Type</th>
                        <th>Sentiment</th>
                        <th>Mentions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredKeywordBreakdown.map((row) => (
                        <tr key={`${row.phrase}-${row.type}-${row.sentiment}`}>
                          <td>{row.phrase}</td>
                          <td>{row.type}</td>
                          <td>{row.sentiment}</td>
                          <td>{row.mentionCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>

              <section className="google-reviews-dashboard-two-column-panels">
                <article className="google-reviews-dashboard-list-card">
                  <h3>Staff Leaderboard</h3>
                  {(data.staffLeaderboard || []).length === 0 ? (
                    <p className="google-reviews-dashboard-empty">No staff leaderboard data available.</p>
                  ) : (
                    <table className="google-reviews-dashboard-table">
                      <thead>
                        <tr>
                          <th>Staff</th>
                          <th>Mentions</th>
                          <th>Positive</th>
                          <th>Neutral</th>
                          <th>Negative</th>
                          <th>Positive %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data.staffLeaderboard || []).map((row) => (
                          <tr key={row.staffName}>
                            <td>{row.staffName}</td>
                            <td>{row.mentionCount}</td>
                            <td>{row.positiveCount}</td>
                            <td>{row.neutralCount}</td>
                            <td>{row.negativeCount}</td>
                            <td>{row.positivePercent == null ? '-' : `${Number(row.positivePercent).toFixed(2)}%`}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </article>
              </section>

              <section className="google-reviews-dashboard-list-card">
                <h3>Company Details</h3>
                {(data.companyDetails || []).length === 0 ? (
                  <p className="google-reviews-dashboard-empty">No company details available.</p>
                ) : (
                  <table className="google-reviews-dashboard-table">
                    <thead>
                      <tr>
                        <th>Business</th>
                        <th>Reviews</th>
                        <th>Avg Rating</th>
                        <th>Avg Lumina</th>
                        <th>Positive %</th>
                        <th>Negative %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.companyDetails || []).map((row) => (
                        <tr key={row.businessName}>
                          <td>{row.businessName}</td>
                          <td>{row.reviewCount}</td>
                          <td>{row.averageRating ?? '-'}</td>
                          <td>{row.averageLuminaScore ?? '-'}</td>
                          <td>{Number(row.positivePercent).toFixed(2)}%</td>
                          <td>{Number(row.negativePercent).toFixed(2)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>

            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GoogleReviewsDashboardPage;
